/**
 * Optimized Lucide icons bundle for platform pages
 * Only includes icons actually used across the platform (not all ~1000 icons)
 * This reduces the bundle from ~85kb to ~25-30kb
 *
 * IMPORTANT: any new data-lucide icon used in a view must be added here in BOTH
 * the import list and the icons object below, or it will silently render nothing.
 */

import {
  createIcons,
  Columns,
  Columns3,
  AlertCircle,
  AlertTriangle,
  Archive,
  ArrowDown,
  ArrowUp,
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  ArrowRightCircle,
  ArrowUpDown,
  ArrowUpWideNarrow,
  Award,
  BadgeCheck,
  Ban,
  Banknote,
  BarChart2,
  BarChart3,
  Bell,
  BellOff,
  Book,
  BookOpen,
  Bookmark,
  BookmarkCheck,
  Box,
  Building,
  Building2,
  Calculator,
  Calendar,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarPlus,
  CalendarX,
  Camera,
  ChartColumn,
  ChartLine,
  Check,
  CheckCircle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Circle,
  CircleCheck,
  CircleCheckBig,
  CircleHelp,
  CircleMinus,
  CirclePlus,
  CircleQuestionMark,
  CircleX,
  Clipboard,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Clock8,
  Code,
  Coins,
  Compass,
  Copy,
  CreditCard,
  DollarSign,
  Download,
  Earth,
  Edit,
  Eye,
  EyeOff,
  ExternalLink,
  FilePen,
  FilePlus,
  FileSignature,
  FileText,
  FileType,
  Flag,
  FileUp,
  Filter,
  Flame,
  FlaskConical,
  Folder,
  FolderOpen,
  Gavel,
  Gift,
  Globe,
  GraduationCap,
  Grip,
  GripVertical,
  Hand,
  HandCoins,
  Handshake,
  Headset,
  Heart,
  History,
  Home,
  Hourglass,
  Image,
  Images,
  Inbox,
  Info,
  Key,
  Landmark,
  Laptop,
  LayoutGrid,
  LayoutList,
  LayoutPanelLeft,
  Layers,
  LifeBuoy,
  Lightbulb,
  Link,
  Linkedin,
  List,
  ListCheck,
  ListChecks,
  Loader,
  Lock,
  LockOpen,
  LogOut,
  Mail,
  MailCheck,
  Map,
  MapPin,
  Megaphone,
  Menu,
  MessageSquare,
  MessagesSquare,
  Mic,
  Minus,
  MinusCircle,
  Monitor,
  Navigation,
  PackagePlus,
  Palette,
  PartyPopper,
  Pause,
  PauseCircle,
  Pencil,
  Percent,
  Phone,
  PhoneCall,
  PiggyBank,
  Pilcrow,
  Play,
  Plus,
  Presentation,
  Printer,
  Puzzle,
  Quote,
  Receipt,
  ReceiptText,
  RefreshCw,
  Reply,
  Rocket,
  RotateCcw,
  Route,
  Rss,
  Save,
  Scale,
  Search,
  Send,
  Settings2,
  Share2,
  Shield,
  ShieldCheck,
  ShieldX,
  ShoppingCart,
  Sliders,
  SlidersHorizontal,
  Smile,
  Snowflake,
  Sparkles,
  SquarePen,
  SquircleDashed,
  Star,
  StarHalf,
  Store,
  Tag,
  Tags,
  Ticket,
  Trophy,
  Trash,
  Trash2,
  TrendingUp,
  TriangleAlert,
  Truck,
  Undo2,
  Unlink,
  Upload,
  User,
  UserCheck,
  UserMinus,
  UserPen,
  UserPlus,
  Users,
  Video,
  X,
  XCircle,
  Zap
} from 'lucide'

const globalScope = typeof window !== 'undefined' ? window : globalThis

if (!globalScope.__lucidePlatformRendererInitialized && typeof document !== 'undefined') {
  globalScope.__lucidePlatformRendererInitialized = true

  let renderScheduled = false

  const renderIcons = () => {
    renderScheduled = false
    createIcons({
      icons: {
        Columns,
        Columns3,
        AlertCircle,
        AlertTriangle,
        Archive,
        ArrowDown,
        ArrowUp,
        ArrowLeft,
        ArrowLeftRight,
        ArrowRight,
        ArrowRightCircle,
        ArrowUpDown,
        ArrowUpWideNarrow,
        Award,
        BadgeCheck,
        Ban,
        Banknote,
        BarChart2,
        BarChart3,
        Bell,
        BellOff,
        Book,
        BookOpen,
        Bookmark,
        BookmarkCheck,
        Box,
        Building,
        Building2,
        Calculator,
        Calendar,
        CalendarCheck,
        CalendarClock,
        CalendarDays,
        CalendarPlus,
        CalendarX,
        Camera,
        ChartColumn,
        ChartLine,
        Check,
        CheckCircle,
        CheckCircle2,
        ChevronDown,
        ChevronLeft,
        ChevronRight,
        ChevronUp,
        Circle,
        CircleCheck,
        CircleCheckBig,
        CircleHelp,
        CircleMinus,
        CirclePlus,
        CircleQuestionMark,
        CircleX,
        Clipboard,
        ClipboardCheck,
        ClipboardList,
        Clock,
        Clock8,
        Code,
        Coins,
        Compass,
        Copy,
        CreditCard,
        DollarSign,
        Download,
        Earth,
        Edit,
        Eye,
        EyeOff,
        ExternalLink,
        FilePen,
        FilePlus,
        FileSignature,
        FileText,
        FileType,
        Flag,
        FileUp,
        Filter,
        Flame,
        FlaskConical,
        Folder,
        FolderOpen,
        Gavel,
        Gift,
        Globe,
        GraduationCap,
        Grip,
        GripVertical,
        Hand,
        HandCoins,
        Handshake,
        Headset,
        Heart,
        History,
        Home,
        Hourglass,
        Image,
        Images,
        Inbox,
        Info,
        Key,
        Landmark,
        Laptop,
        LayoutGrid,
        LayoutList,
        LayoutPanelLeft,
        Layers,
        LifeBuoy,
        Lightbulb,
        Link,
        Linkedin,
        List,
        ListCheck,
        ListChecks,
        Loader,
        Lock,
        LockOpen,
        LogOut,
        Mail,
        MailCheck,
        Map,
        MapPin,
        Megaphone,
        Menu,
        MessageSquare,
        MessagesSquare,
        Mic,
        Minus,
        MinusCircle,
        Monitor,
        Navigation,
        PackagePlus,
        Palette,
        PartyPopper,
        Pause,
        PauseCircle,
        Pencil,
        Percent,
        Phone,
        PhoneCall,
        PiggyBank,
        Pilcrow,
        Play,
        Plus,
        Presentation,
        Printer,
        Puzzle,
        Quote,
        Receipt,
        ReceiptText,
        RefreshCw,
        Reply,
        Rocket,
        RotateCcw,
        Route,
        Rss,
        Save,
        Scale,
        Search,
        Send,
        Settings2,
        Share2,
        Shield,
        ShieldCheck,
        ShieldX,
        ShoppingCart,
        Sliders,
        SlidersHorizontal,
        Smile,
        Snowflake,
        Sparkles,
        SquarePen,
        SquircleDashed,
        Star,
        StarHalf,
        Store,
        Tag,
        Tags,
        Ticket,
        Trophy,
        Trash,
        Trash2,
        TrendingUp,
        TriangleAlert,
        Truck,
        Undo2,
        Unlink,
        Upload,
        User,
        UserCheck,
        UserMinus,
        UserPen,
        UserPlus,
        Users,
        Video,
        X,
        XCircle,
        Zap
      }
    })
  }

  const scheduleRender = () => {
    if (renderScheduled) return
    renderScheduled = true
    ;(globalScope.requestAnimationFrame || globalScope.setTimeout)(renderIcons)
  }

  const hasPendingIcons = (root = document) =>
    root.querySelector?.('[data-lucide]:not(svg)') ||
    root.querySelector?.('svg[data-lucide]:not(.lucide)')

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'data-lucide') {
        scheduleRender()
        return
      }

      if (mutation.type === 'childList') {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE && hasPendingIcons(node)) {
            scheduleRender()
            return
          }
        }
      }
    }
  })

  const start = () => {
    scheduleRender()
    observer.disconnect()
    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-lucide']
      })
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true })
  } else {
    start()
  }

  document.addEventListener('turbo:load', start)
  document.addEventListener('turbolinks:load', start)

  globalScope.refreshLucideIcons = () => scheduleRender()
}

